/* eslint-disable @typescript-eslint/ban-ts-comment */
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { type Observable } from 'rxjs'
import { SharedService } from './shared.service'
import { type hr as cpt } from 'efd-cpt-backend-interfaces-ts'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { type CustomerPortalConfig } from '@inside-hub-app/customer-portal-config'

export type MultileaseContactDataDTO =
  cpt.emilfreydigital.customer_portal_backend.rest.loan.dto.MultileaseContactDataDTO
export type MultileaseContactRequestDTO =
  cpt.emilfreydigital.customer_portal_backend.rest.loan.dto.MultileaseContactRequestDTO
export type LoanContactDTO =
  cpt.emilfreydigital.customer_portal_backend.rest.loan.dto.LoanContactDTO

export type LoanContactDataDto =
  cpt.emilfreydigital.customer_portal_backend.rest.vehicle_loan.dto.LoanContactDataDto
export type LoanContactRequestDto =
  cpt.emilfreydigital.customer_portal_backend.rest.vehicle_loan.dto.LoanContactRequestDto
export type LoanExtensionRequestDto =
  cpt.emilfreydigital.customer_portal_backend.rest.vehicle_loan.dto.LoanExtensionRequestDto
export type VehicleLoanDto =
  cpt.emilfreydigital.customer_portal_backend.rest.vehicle_loan.dto.VehicleLoanDto

export interface LeasingCalculations {
  totalDaysLength: number
  tillExpires: number
  percentage: number
  percentageLeft: number
  months: number
  years: number
}

export enum LoanType {
  FINANCING = 'FINANCING',
  LEASING = 'LEASING'
}

@Injectable({
  providedIn: 'root'
})
export class LeasingService {
  private readonly apiUrl: string
  private readonly newApiEnabled: boolean
  token: string

  constructor (
    private readonly http: HttpClient,
    private readonly sharedService: SharedService,
    private readonly remoteConfigService: EfRemoteConfigurationService<CustomerPortalConfig>
  ) {
    this.apiUrl = this.remoteConfigService.get('backend.url')
    this.newApiEnabled = this.remoteConfigService.get('ownership.new-api.enabled')
  }

  /**
   * Get Multilease loan contact data.
   * Flag ON + vin: GET /v1/vehicles/{vin}/loan-contact-form-data  (LoanContactDataDto)
   * Flag OFF and no vin: GET /loan/multilease-contact/{contractNumber}  (legacy, MultileaseContactDataDTO)
   */
  public getMultiLeaseLoanContactData (
    contractNumber: string,
    vin?: string
  ): Observable<LoanContactDataDto | MultileaseContactDataDTO> {
    if (this.newApiEnabled && this.sharedService.stringExists(vin)) {
      return this.http.get<LoanContactDataDto>(
        `${this.apiUrl}/v1/vehicles/${vin}/loan-contact-form-data`
      )
    }
    return this.http.get<MultileaseContactDataDTO>(
      `${this.apiUrl}/loan/multilease-contact/${contractNumber}`
    )
  }

  /**
   * Send Multilease loan contact request.
   * Flag ON + vin: POST /v1/vehicles/{vin}/loan-contact-requests
   *   body: LoanContactRequestDto { number, reasonId, affiliateId, message, mileage }
   * Flag OFF or no vin: POST /loan/multilease-contact/{contractNumber}  (legacy, full dto body)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public sendMultileaseContactForm (
    contractNumber: string,
    data: MultileaseContactRequestDTO,
    lang?: string,
    vin?: string
  ): Observable<any> {
    let params = new HttpParams()
    if (this.sharedService.stringExists(lang)) {
      params = params.append('lang', lang)
    }

    if (this.newApiEnabled && this.sharedService.stringExists(vin)) {
      const body: LoanContactRequestDto = {
        number: contractNumber,
        reasonId: data.reasonId,
        affiliateId: data.affiliateId,
        message: data.message,
        mileage: data.mileage
      }
      return this.http.post(
        `${this.apiUrl}/v1/vehicles/${vin}/loan-contact-requests`,
        body,
        { params }
      )
    }

    return this.http.post(
      `${this.apiUrl}/loan/multilease-contact/${contractNumber}`,
      data,
      { params }
    )
  }

  /**
   * Send loan extension request.
   * Flag ON + vin + contractNumber: POST /v1/vehicles/{vin}/loan-extension-requests
   *   body: LoanExtensionRequestDto { number, message }
   * Flag OFF or missing vin/contractNumber: POST /loan/contact  (legacy, full dto body)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public sendContactForm (
    data: LoanContactDTO,
    lang?: string,
    vin?: string,
    contractNumber?: string
  ): Observable<any> {
    let params = new HttpParams()
    if (this.sharedService.stringExists(lang)) {
      params = params.append('lang', lang)
    }

    if (
      this.newApiEnabled &&
      this.sharedService.stringExists(vin) &&
      this.sharedService.stringExists(contractNumber)
    ) {
      const body: LoanExtensionRequestDto = {
        number: contractNumber,
        message: data.message
      }
      return this.http.post(
        `${this.apiUrl}/v1/vehicles/${vin}/loan-extension-requests`,
        body,
        { params }
      )
    }

    return this.http.post(`${this.apiUrl}/loan/contact`, data, { params })
  }

  calculateDays (leasing: VehicleLoanDto): LeasingCalculations {
    const oneDay = 24 * 60 * 60 * 1000
    let firstDay: Date
    let lastDay: Date
    const currentDay = new Date()
    let years = 0
    let months = 0

    if (leasing.startDate != null && leasing.endDate != null) {
      firstDay = new Date(leasing.startDate)
      lastDay = new Date(leasing.endDate)
    } else if (leasing.startDate != null && leasing.months != null) {
      firstDay = new Date(leasing.startDate)
      lastDay = new Date(
        firstDay.setMonth(firstDay.getMonth() + leasing.months)
      )
    }

    years = lastDay.getFullYear() - currentDay.getFullYear()
    months = this.sharedService.monthDiff(currentDay, lastDay)

    if (years * 12 > months) {
      years = years - 1
    }

    // @ts-expect-error
    const diffDays = Math.round(Math.abs((firstDay - lastDay) / oneDay))
    // @ts-expect-error
    const numOfDaysPast = Math.round(Math.abs((firstDay - currentDay) / oneDay))

    if (diffDays - numOfDaysPast < 0) {
      return null
    } else {
      return {
        totalDaysLength: diffDays,
        tillExpires: diffDays - numOfDaysPast,
        percentage: (numOfDaysPast / diffDays) * 100,
        percentageLeft: 100 - (numOfDaysPast / diffDays) * 100,
        months,
        years
      }
    }
  }
}
